import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CurrencyInput from 'react-currency-input-field';
import Swal from 'sweetalert2';
import { ChevronLeft, ChevronRight, Plus, Search, Tag, Trash2, UserPen } from 'lucide-react';

function PricePage() {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);

    const [form, setForm] = useState({
        id: null,
        code: '',
        name: '',
        price: '',
        status: 'ACTIVE',
        session_create_max: '',
        chat_attempt_max: '',
        max_ai_token: '',
        is_ai_audio_active: 0,
        voice_attempt_max: '',
        examination_assist_max: '',
        duration_type: '',
        duration_value: 0
    });

    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(false);

    const fetchData = async () => {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/datatable/pricelist`, {
            params: { start: page * limit, length: limit, search },
            withCredentials: true,
        });
        setData(res.data.data);
        setTotal(res.data.recordsFiltered);
    };

    useEffect(() => {
        fetchData();
    }, [page, search, limit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.code || !form.name || !form.price || !form.session_create_max || !form.chat_attempt_max || !form.max_ai_token) {
            Swal.fire('Oops!', 'Semua field harus diisi.', 'error');
            return;
        }

        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/admin/pricelist`, form, { withCredentials: true });
            setShowModal(false);
            fetchData();
            resetForm();
            Swal.fire('Berhasil', 'Data berhasil disimpan', 'success');
        } catch (error) {
            Swal.fire('Oops!', (error.response?.data?.message || "Silakan coba lagi."), 'error');
        }
    };

    const resetForm = () => {
        setForm({
            id: null,
            code: '',
            name: '',
            price: '',
            status: 'ACTIVE',
            session_create_max: '',
            chat_attempt_max: '',
            max_ai_token: '',
            is_ai_audio_active: 0,
            voice_attempt_max: '',
            examination_assist_max: '',
            duration_type: '',
            duration_value: 0
        });
        setEditing(false);
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    const editForm = async (item) => {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/pricelist/${item.id}`, { withCredentials: true });
        setForm(res.data);
        setEditing(true);
        setShowModal(true);
    };

    const deleteItem = async (id) => {

        const result = await Swal.fire({
            title: 'Yakin ingin menghapus data ini',
            text: 'Data akan dihapus secara permanen.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ya, hapus!',
            cancelButtonText: 'Batal'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/admin/pricelist/${id}`, { withCredentials: true });
                Swal.fire('Berhasil', 'Data berhasil dihapus.', 'success');
                fetchData();
            } catch (err) {
                Swal.fire('Oops!', (err.response?.data?.message || "Silakan coba lagi."), 'error');
            }
        }

    };

    return (
        <div className="container-fluid px-0">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-4 flex items-center gap-2 text-slate-800">
                    <Tag size={18} />
                    <h4 className="mb-0 text-lg font-semibold">Daftar Pricelist</h4>
                </div>

                <div className="mb-4 d-flex flex-wrap align-items-end justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Tampilkan</span>
                        <select className="form-select form-select-sm rounded-lg border-slate-300" style={{ width: '88px' }} value={limit} onChange={(e) => { setPage(0); setLimit(Number(e.target.value)); }}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span className="small text-muted">data</span>
                    </div>

                    <div className="d-flex flex-wrap align-items-end gap-3">
                        <div className="min-w-[280px] max-w-[360px] flex-1">
                            <div className="relative">
                                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                    <Search size={16} />
                                </span>
                                <input
                                    type="text"
                                    className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                    placeholder="Cari kode atau nama paket..."
                                    value={search}
                                    onChange={(e) => { setPage(0); setSearch(e.target.value); }}
                                />
                            </div>
                        </div>
                        <button className="btn btn-dark d-inline-flex align-items-center gap-2" onClick={openCreateModal}><Plus size={16} /> Tambah Data Baru</button>
                    </div>
                </div>

                <div className='table-responsive rounded-xl border border-slate-300 bg-white'>
                    <table className="table table-hover table-striped table-bordered table-sm mb-0">
                        <thead>
                            <tr>
                                <th className='bg-light'>Kode</th>
                                <th className='bg-light'>Nama</th>
                                <th className='bg-light'>Harga</th>
                                <th className='bg-light'>Max. Session</th>
                                <th className='bg-light'>Max. Chat Attempt</th>
                                <th className='bg-light'>Max. AI Token</th>
                                <th className='bg-light'>Voice to Voice Feature?</th>
                                <th className='bg-light'>Max. Voice Attempt</th>
                                <th className='bg-light'>Max. Examination Assistant Attempt</th>
                                <th className='bg-light'>Durasi</th>
                                <th className='bg-light'>Status</th>
                                <th className='text-center bg-light' width="10%">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.code}</td>
                                    <td>{item.name}</td>
                                    <td>Rp {parseInt(item.price).toLocaleString('id-ID')}</td>
                                    <td>{item.session_create_max == -1 ? 'Unlimited' : item.session_create_max}</td>
                                    <td>{item.chat_attempt_max == -1 ? 'Unlimited' : item.chat_attempt_max}</td>
                                    <td>{item.max_ai_token}</td>
                                    <td>{item.is_ai_audio_active == 1 ? 'ACTIVE' : 'INACTIVE'}</td>
                                    <td>{item.voice_attempt_max == -1 ? 'Unlimited' : item.voice_attempt_max}</td>
                                    <td>{item.examination_assist_max == -1 ? 'Unlimited' : item.examination_assist_max}</td>
                                    <td>
                                        {item.duration_type == 'Permanent' ? (
                                            <>{item.duration_type}</>
                                        ) : (
                                            <>{item.duration_value} {item.duration_type}(s)</>
                                        )}
                                    </td>
                                    <td>{item.status}</td>
                                    <td className="text-center">
                                        <button className="btn btn-sm btn-outline-dark me-1" onClick={() => editForm(item)}><UserPen size={14} /></button>
                                        <button className="btn btn-sm btn-outline-dark" onClick={() => deleteItem(item.id)}><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-3 d-flex justify-content-start align-items-center gap-3 text-sm text-slate-500">
                    <small>Total: {total}</small>
                    <div className="d-inline-flex align-items-center gap-2 rounded-pill border border-slate-200 bg-white px-2 py-1 shadow-sm">
                        <button className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Halaman sebelumnya"><ChevronLeft size={18} /></button>
                        <span className="px-2 small text-muted">{page + 1} / {Math.max(1, Math.ceil(total / limit))}</span>
                        <button className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center" disabled={(page + 1) * limit >= total} onClick={() => setPage(page + 1)} aria-label="Halaman berikutnya"><ChevronRight size={18} /></button>
                    </div>
                </div>
            </div>

            {showModal && (
                <div className="modal d-block" tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <form onSubmit={handleSubmit}>
                                <div className="modal-header">
                                    <h5 className="modal-title">{editing ? 'Edit' : 'Tambah'} Pricelist</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body row g-2">
                                    <div className="col-md-6">
                                        <label>Kode</label>
                                        <input type="text" className="form-control" name="code" value={form.code} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Nama</label>
                                        <input type="text" className="form-control" name="name" value={form.name} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Harga</label>
                                        <CurrencyInput
                                            className="form-control"
                                            name="price"
                                            value={form.price}
                                            onValueChange={(val) => setForm(prev => ({ ...prev, price: val }))}
                                            decimalsLimit={2}
                                            prefix="Rp "
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Max. Session (Unlimited set : -1)</label>
                                        <input type="number" className="form-control" name="session_create_max" value={form.session_create_max} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Max. Chat Attempt (Unlimited set : -1)</label>
                                        <input type="number" className="form-control" name="chat_attempt_max" value={form.chat_attempt_max} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Max. AI Token</label>
                                        <input type="number" className="form-control" name="max_ai_token" value={form.max_ai_token} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label>AI Audio to Audio Feature</label>
                                        <select className="form-control" name="is_ai_audio_active" value={form.is_ai_audio_active} onChange={handleChange} required>
                                            <option value="0">INACTIVE</option>
                                            <option value="1">ACTIVE</option>
                                        </select>
                                    </div>
                                    {form.is_ai_audio_active == 1 && (
                                        <div className="col-md-6">
                                            <label>Max. Voice Attempt (Unlimited set: -1)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="voice_attempt_max"
                                                value={form.voice_attempt_max}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    )}


                                    <div className="col-md-6">
                                        <label>Max. Examination Assistant Attempt (Unlimited set: -1)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="examination_assist_max"
                                            value={form.examination_assist_max}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>Tipe Durasi</label>
                                        <select className="form-control" name="duration_type" value={form.duration_type} onChange={handleChange} required>
                                            <option value="">Silahkan Pilih</option>
                                            <option value="Day">Day</option>
                                            <option value="Week">Week</option>
                                            <option value="Month">Month</option>
                                            <option value="Year">Year</option>
                                            <option value="Permanent">Permanent</option>
                                        </select>
                                    </div>
                                    {form.duration_type != 'Permanent' && form.duration_type != '' && (
                                        <div className="col-md-6">
                                            <label>Durasi (Sesuai Tipe)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder=""
                                                name="duration_value"
                                                min="0"
                                                value={form.duration_value}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    )}
                                    <div className="col-md-6">
                                        <label>Status</label>
                                        <select className="form-control" name="status" value={form.status} onChange={handleChange} required>
                                            <option value="ACTIVE">ACTIVE</option>
                                            <option value="INACTIVE">INACTIVE</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="submit" className="btn btn-dark">{editing ? 'Update' : 'Simpan'}</button>
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Tutup</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PricePage;
